import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  Download,
  ArrowBack,
  Refresh,
  TrendingUp,
  TrendingDown,
  Equalizer,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ShowChart,
  CalendarToday,
  Info
} from '@mui/icons-material';
import reportService from '../../../services/reportService';
import excelExport from '../../../utils/excelExport';
import DateRangeFilter from './DateRangeFilter';
import './SalesReports.css';

const SalesReports = ({ onPageChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('daily_sales_amount');
  const [reportData, setReportData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    days: 30,
    months: 12
  });

  const reportTypes = [
    { 
      value: 'daily_sales_amount', 
      label: 'Daily Sales',
      icon: <ShowChart />,
      description: 'Daily sales performance across all channels'
    },
    { 
      value: 'daily_sales', 
      label: 'Sales Summary',
      icon: <Equalizer />,
      description: 'Detailed daily sales breakdown'
    },
    { 
      value: 'sales_comparison', 
      label: 'Channel Comparison',
      icon: <BarChartIcon />,
      description: 'POS vs Ecommerce performance'
    },
    { 
      value: 'peak_sales_hours', 
      label: 'Peak Hours',
      icon: <TrendingUp />,
      description: 'Busiest sales hours analysis'
    },
    { 
      value: 'monthly_performance', 
      label: 'Monthly Trends',
      icon: <CalendarToday />,
      description: 'Monthly performance overview'
    },
    { 
      value: 'payment_methods', 
      label: 'Payment Methods',
      icon: <PieChartIcon />,
      description: 'Payment method distribution'
    }
  ];

  // Color schemes
  const chartColors = {
    pos: '#6366F1',
    ecommerce: '#10B981',
    total: '#F59E0B',
    orders: '#8B5CF6',
    revenue: '#EF4444',
    secondary: '#6B7280'
  };

  const statusColors = {
    increase: '#10B981',
    decrease: '#EF4444',
    neutral: '#6B7280'
  };

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      switch (reportType) {
        case 'daily_sales':
          response = await reportService.getDailySales(filters);
          break;
        case 'daily_sales_amount':
          response = await reportService.getDailySalesAmount(filters);
          break;
        case 'sales_comparison':
          response = await reportService.getSalesComparison(filters);
          break;
        case 'peak_sales_hours':
          response = await reportService.getPeakSalesHours(filters);
          break;
        case 'monthly_performance':
          response = await reportService.getMonthlyPerformance(filters);
          break;
        case 'payment_methods':
          response = await reportService.getPaymentMethodReport(filters);
          break;
        default:
          response = await reportService.getDailySalesAmount(filters);
      }
      setReportData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load report data: ' + (err.message || 'Unknown error'));
      console.error('Report loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;

    try {
      switch (reportType) {
        case 'daily_sales_amount':
          excelExport.exportDailySales(reportData);
          break;
        case 'payment_methods':
          excelExport.exportPaymentMethods(reportData);
          break;
        case 'sales_comparison':
          excelExport.exportSalesComparison(reportData);
          break;
        case 'peak_sales_hours':
          excelExport.exportPeakSalesHours(reportData);
          break;
        case 'monthly_performance':
          excelExport.exportMonthlyPerformance(reportData);
          break;
        default:
          const exportData = Object.entries(reportData)
            .filter(([key, value]) => Array.isArray(value) && value.length > 0)
            .flatMap(([key, value]) => value);

          if (exportData.length > 0) {
            excelExport.exportToExcel(exportData, `${reportType}_report`);
          } else {
            setError('No data available for export');
          }
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return { value: 0, direction: 'neutral' };
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      direction: change > 0 ? 'increase' : change < 0 ? 'decrease' : 'neutral'
    };
  };

  const renderMetricCard = (title, value, previousValue, format = 'currency', subtitle = '') => {
    const trend = calculateTrend(value, previousValue);
    const formattedValue = format === 'currency' 
      ? `LKR ${value?.toLocaleString() || '0'}` 
      : value?.toLocaleString() || '0';

    return (
      <Card className="sales-metric-card">
        <CardContent className="sales-metric-content">
          <div className="sales-metric-header">
            <Typography className="sales-metric-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="sales-metric-info" />
              </Tooltip>
            )}
          </div>
          <Typography className="sales-metric-value">
            {formattedValue}
          </Typography>
          {trend.direction !== 'neutral' && (
            <div className={`sales-metric-trend sales-trend-${trend.direction}`}>
              {trend.direction === 'increase' ? (
                <TrendingUp className="sales-trend-icon" />
              ) : (
                <TrendingDown className="sales-trend-icon" />
              )}
              <Typography className="sales-trend-text">
                {trend.value}% {trend.direction === 'increase' ? 'increase' : 'decrease'}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDailySalesChart = () => {
    if (!reportData?.daily_sales) return null;

    return (
      <div className="sales-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={reportData.daily_sales} className="sales-area-chart">
            <defs>
              <linearGradient id="posSalesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColors.pos} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={chartColors.pos} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="ecommerceSalesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColors.ecommerce} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={chartColors.ecommerce} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid className="sales-chart-grid" />
            <XAxis dataKey="date" className="sales-chart-axis" />
            <YAxis 
              tickFormatter={(value) => `LKR ${value / 1000}k`}
              className="sales-chart-axis"
            />
            <RechartsTooltip
              formatter={(value) => [`LKR ${value.toLocaleString()}`, 'Amount']}
              className="sales-chart-tooltip"
            />
            <Legend className="sales-chart-legend" />
            <Area
              type="monotone"
              dataKey="pos_sales"
              stroke={chartColors.pos}
              fill="url(#posSalesGradient)"
              strokeWidth={2}
              name="POS Sales"
            />
            <Area
              type="monotone"
              dataKey="ecommerce_sales"
              stroke={chartColors.ecommerce}
              fill="url(#ecommerceSalesGradient)"
              strokeWidth={2}
              name="Ecommerce Sales"
            />
            <Line
              type="monotone"
              dataKey="total_sales"
              stroke={chartColors.total}
              strokeWidth={3}
              dot={false}
              name="Total Sales"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderSalesComparisonChart = () => {
    if (!reportData?.comparison_data) return null;

    return (
      <div className="sales-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={reportData.comparison_data} className="sales-line-chart">
            <CartesianGrid className="sales-chart-grid" />
            <XAxis dataKey="date" className="sales-chart-axis" />
            <YAxis 
              tickFormatter={(value) => `LKR ${value / 1000}k`}
              className="sales-chart-axis"
            />
            <RechartsTooltip
              formatter={(value) => [`LKR ${value.toLocaleString()}`, 'Revenue']}
              className="sales-chart-tooltip"
            />
            <Legend className="sales-chart-legend" />
            <Line
              type="monotone"
              dataKey="pos.revenue"
              stroke={chartColors.pos}
              name="POS Revenue"
              strokeWidth={3}
              dot={{ fill: chartColors.pos, strokeWidth: 2, r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="ecommerce.revenue"
              stroke={chartColors.ecommerce}
              name="Ecommerce Revenue"
              strokeWidth={3}
              dot={{ fill: chartColors.ecommerce, strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderPeakHoursChart = () => {
    if (!reportData?.peak_hours) return null;

    return (
      <div className="sales-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={reportData.peak_hours} className="sales-bar-chart">
            <CartesianGrid className="sales-chart-grid" />
            <XAxis dataKey="formatted_hour" className="sales-chart-axis" />
            <YAxis className="sales-chart-axis" />
            <RechartsTooltip className="sales-chart-tooltip" />
            <Legend className="sales-chart-legend" />
            <Bar 
              dataKey="total_orders" 
              fill={chartColors.orders} 
              name="Orders"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="total_revenue" 
              fill={chartColors.revenue} 
              name="Revenue"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderPaymentMethodChart = () => {
    if (!reportData?.pos_payments) return null;

    const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

    const pieData = reportData.pos_payments.map(payment => ({
      name: payment.payment_method,
      value: parseFloat(payment.total_amount) || 0
    }));

    return (
      <div className="sales-payment-container">
        <div className="sales-pie-chart">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart className="sales-pie-chart-inner">
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value) => [`LKR ${value.toLocaleString()}`, 'Amount']}
                className="sales-chart-tooltip"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="sales-payment-legend">
          {pieData.map((entry, index) => (
            <div key={entry.name} className="sales-payment-item">
              <div 
                className="sales-payment-color" 
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <Typography className="sales-payment-label">{entry.name}</Typography>
              <Typography className="sales-payment-value">
                LKR {entry.value.toLocaleString()}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthlyPerformanceChart = () => {
    if (!reportData?.monthly_comparison) return null;

    return (
      <div className="sales-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={reportData.monthly_comparison} className="sales-bar-chart">
            <CartesianGrid className="sales-chart-grid" />
            <XAxis dataKey="month_name" className="sales-chart-axis" />
            <YAxis 
              tickFormatter={(value) => `LKR ${value / 1000}k`}
              className="sales-chart-axis"
            />
            <RechartsTooltip 
              formatter={(value) => [`LKR ${value.toLocaleString()}`, 'Revenue']}
              className="sales-chart-tooltip"
            />
            <Legend className="sales-chart-legend" />
            <Bar 
              dataKey="pos.total_revenue" 
              fill={chartColors.pos} 
              name="POS Revenue"
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="ecommerce.total_revenue" 
              fill={chartColors.ecommerce} 
              name="Ecommerce Revenue"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderDataTable = () => {
    if (!reportData) return null;

    switch (reportType) {
      case 'daily_sales_amount':
        return (
          <TableContainer className="sales-table-container">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className="sales-table-header-cell">Date</TableCell>
                  <TableCell align="right" className="sales-table-header-cell">POS Sales</TableCell>
                  <TableCell align="right" className="sales-table-header-cell">Ecommerce Sales</TableCell>
                  <TableCell align="right" className="sales-table-header-cell">Total Sales</TableCell>
                  <TableCell align="right" className="sales-table-header-cell">Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.daily_sales?.map((row, index) => (
                  <TableRow key={index} className="sales-table-row">
                    <TableCell>
                      <Typography className="sales-table-date">{row.date}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="sales-table-pos">
                        LKR {row.pos_sales?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="sales-table-ecom">
                        LKR {row.ecommerce_sales?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className="sales-table-total">
                        LKR {row.total_sales?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={row.total_transactions} 
                        className="sales-transaction-chip"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'payment_methods':
        return (
          <div className="sales-payment-tables">
            <Card className="sales-payment-card">
              <CardContent className="sales-payment-content">
                <Typography className="sales-payment-title">POS Payments</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className="sales-table-header-cell">Payment Method</TableCell>
                        <TableCell align="right" className="sales-table-header-cell">Transactions</TableCell>
                        <TableCell align="right" className="sales-table-header-cell">Total Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.pos_payments?.map((payment, index) => (
                        <TableRow key={index} className="sales-table-row">
                          <TableCell>
                            <Typography className="sales-payment-method">
                              {payment.payment_method}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={payment.transaction_count} 
                              className="sales-transaction-chip"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography className="sales-payment-amount">
                              LKR {payment.total_amount?.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card className="sales-payment-card">
              <CardContent className="sales-payment-content">
                <Typography className="sales-payment-title">Ecommerce Payments</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className="sales-table-header-cell">Payment Method</TableCell>
                        <TableCell align="right" className="sales-table-header-cell">Transactions</TableCell>
                        <TableCell align="right" className="sales-table-header-cell">Total Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.ecommerce_payments?.map((payment, index) => (
                        <TableRow key={index} className="sales-table-row">
                          <TableCell>
                            <Typography className="sales-payment-method">
                              {payment.payment_method}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={payment.transaction_count} 
                              className="sales-transaction-chip"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography className="sales-payment-amount">
                              LKR {payment.total_amount?.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return (
          <div className="sales-no-table">
            <Typography className="sales-no-table-text">
              No table data available for this report type.
            </Typography>
          </div>
        );
    }
  };

  const renderChartContent = () => {
    switch (reportType) {
      case 'daily_sales_amount':
        return renderDailySalesChart();
      case 'sales_comparison':
        return renderSalesComparisonChart();
      case 'peak_sales_hours':
        return renderPeakHoursChart();
      case 'payment_methods':
        return renderPaymentMethodChart();
      case 'monthly_performance':
        return renderMonthlyPerformanceChart();
      default:
        return renderDailySalesChart();
    }
  };

  const currentReport = reportTypes.find(r => r.value === reportType);

  return (
    <Box className="sales-reports-container">
      {/* Header */}
      <Card className="sales-header-card">
        <CardContent className="sales-header-content">
          <div className="sales-header-main">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => onPageChange('all-reports')}
              className="sales-back-button"
            >
              Back to Reports
            </Button>
            <div className="sales-header-text">
              <Typography className="sales-header-title">Sales Analytics</Typography>
              <Typography className="sales-header-subtitle">
                {currentReport?.description}
              </Typography>
            </div>
          </div>
          <div className="sales-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={loadReportData} 
                disabled={loading}
                className="sales-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Download />}
              onClick={handleExport}
              disabled={!reportData || loading}
              className="sales-export-button"
            >
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Type Selection */}
      <Card className="sales-controls-card">
        <CardContent className="sales-controls-content">
          <Typography className="sales-controls-title">Report Type</Typography>
          <div className="sales-controls-grid">
            <FormControl fullWidth className="sales-select-control">
              <InputLabel>Select Report</InputLabel>
              <Select
                value={reportType}
                label="Select Report"
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <div className="sales-menu-item">
                      {type.icon}
                      {type.label}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DateRangeFilter
              filters={filters}
              onFilterChange={setFilters}
              showDays={reportType === 'peak_sales_hours' || reportType === 'sales_comparison'}
              showMonths={reportType === 'monthly_performance'}
              showPeriod={reportType === 'sales_comparison'}
            />
          </div>
          {lastUpdated && (
            <div className="sales-last-updated">
              <Typography className="sales-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          className="sales-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadReportData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="sales-loading-container">
          <CircularProgress size={60} className="sales-loading-spinner" />
          <Typography className="sales-loading-text">
            Loading report data...
          </Typography>
        </div>
      ) : reportData ? (
        <div className="sales-report-content">
          {/* Summary Metrics */}
          {reportData.summary && (
            <div className="sales-metrics-grid">
              {Object.entries(reportData.summary).map(([key, value], index) => (
                <div key={key} className="sales-metric-item">
                  {renderMetricCard(
                    key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
                    value,
                    reportData.previous_period?.[key],
                    key.includes('revenue') || key.includes('amount') || key.includes('profit') ? 'currency' : 'number',
                    `Current ${key.replace('_', ' ')}`
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Main Chart */}
          <Card className="sales-chart-card">
            <CardContent className="sales-chart-content">
              <div className="sales-chart-header">
                {currentReport?.icon}
                <Typography className="sales-chart-title">
                  {currentReport?.label} Analysis
                </Typography>
              </div>
              {renderChartContent()}
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card className="sales-table-card">
            <CardContent className="sales-table-content">
              <div className="sales-table-header">
                <Equalizer className="sales-table-icon" />
                <Typography className="sales-table-title">Detailed Data</Typography>
              </div>
              {renderDataTable()}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="sales-empty-card">
          <CardContent className="sales-empty-content">
            <Typography className="sales-empty-text">
              No sales data available for the selected report type.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SalesReports;